/** A sentence chosen to present a word in context, plus where the word sits in it. */
import { hasLength } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";

export type WordContextSentence = {
  /** 0-based grapheme-cluster offset of the target word within `sentence`. */
  charOffset: number;
  /** The sentence itself — also its identity (static-DB sentence ids renumber per build). */
  sentence: string;
  /** The English translation — the drill's prompt, so candidates without one are unusable. */
  translation: string;
};

export type WordContextSentenceCandidate = {
  charOffset: number;
  sentence: string;
  translation: string | null;
};

/**
 * Pick which sentence presents a word this review.
 *
 * The choice must be STABLE within a single review but VARY across reviews. Stability matters
 * because the drill's back is reached by navigation, not a state flip — an unstable pick would
 * show a different sentence on the back than the front just did. Variation matters because
 * seeing the same example every time is what makes a word card degenerate into memorizing one
 * sentence, which is exactly what the design set out to avoid.
 *
 * Both fall out of deriving the index from `(userCardId, reviewCount)` rather than from
 * randomness: the same card at the same review count always resolves to the same sentence, and
 * the next review advances it. This also keeps the function pure, so it is directly testable
 * and safe to call during render.
 *
 * Candidates without a translation are dropped: the translation IS the prompt, so a sentence
 * without one cannot be drilled. Returns `null` when nothing usable remains — callers must
 * handle that rather than assume a sentence exists.
 */
export function selectWordContextSentence({
  candidates,
  reviewCount,
  userCardId,
}: {
  candidates: readonly WordContextSentenceCandidate[];
  reviewCount: number;
  userCardId: string;
}): WordContextSentence | null {
  const usable = candidates.flatMap((candidate) =>
    candidate.translation === null || candidate.translation.length === 0
      ? []
      : [{ ...candidate, translation: candidate.translation }],
  );
  if (hasLength(usable, 0)) {
    return null;
  }
  // Sort by the sentence text so the ordering depends neither on however the DB returned the rows
  // (the "stable within a review" guarantee must not rest on query-plan ordering) nor on static
  // sentence ids, which renumber on every static rebuild and would reshuffle every rotation.
  const ordered = [...usable].sort((a, b) =>
    a.sentence < b.sentence ? -1 : a.sentence > b.sentence ? 1 : 0,
  );
  const offset = hashString(userCardId) + Math.max(0, Math.trunc(reviewCount));
  return ordered[offset % arrayLength(ordered)] ?? null;
}

/**
 * FNV-1a. A deterministic spread over card ids so two cards with adjacent ids don't march
 * through their candidate lists in lockstep. Not security-relevant.
 */
function hashString(value: string): number {
  let hash = 2_166_136_261;
  for (const character of value) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 16_777_619);
  }
  return Math.abs(hash);
}
